import { useState } from 'react';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { SevPill } from '@/components/brand/SevPill';
import { useApiQuery } from '@/lib/hooks';
import { api, ApiError, type AutomationItem } from '@/lib/api';
import { EmptyState, ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';

function relative(ms: number): string {
  const now = Date.now();
  const diff = Math.max(0, now - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function statusLevel(s: string): 'ok' | 'warn' | 'crit' | 'info' {
  if (s === 'failed' || s === 'timeout') return 'crit';
  if (s === 'warning') return 'warn';
  if (s === 'info') return 'info';
  return 'ok';
}

import { sevenDayWindow } from '@/lib/time';

export function AutomationsPage() {
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const { from, to: now } = sevenDayWindow();

  const q = useApiQuery<{ items: AutomationItem[]; next_cursor?: string }>({
    queryKey: ['automations', source, status, cursor],
    queryFn: () => api.automations({ from, to: now, source: source || undefined, status: status || undefined, cursor }),
  });

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Activity" title="Automations" meta="Last 7 days" />
      <Panel title="Filters">
        <div className="flex flex-wrap gap-3 text-small items-end">
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Source</span>
            <select value={source} onChange={(e) => { setSource(e.target.value); setCursor(undefined); }} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="hubspot">hubspot</option>
              <option value="cloudflare_worker">cloudflare_worker</option>
              <option value="sap_b1">sap_b1</option>
              <option value="m365">m365</option>
              <option value="anthropic">anthropic</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Status</span>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setCursor(undefined); }} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="success">success</option>
              <option value="failed">failed</option>
              <option value="timeout">timeout</option>
              <option value="warning">warning</option>
              <option value="info">info</option>
            </select>
          </label>
          {(source || status || cursor) && (
            <button type="button" onClick={() => { setSource(''); setStatus(''); setCursor(undefined); }} className="text-eyebrow text-fg-muted border border-rule px-3 py-2">
              Reset
            </button>
          )}
        </div>
      </Panel>
      <Panel title="Recent runs" subtitle={q.data ? `${q.data.items.length} shown` : undefined}>
        {q.isLoading && <LoadingState />}
        {q.error instanceof ApiError && q.error.isUnauthorized() && <UnauthorizedState />}
        {q.error && !(q.error instanceof ApiError && q.error.isUnauthorized()) && (
          <ErrorState title="Couldn't load automations" detail={q.error.message} />
        )}
        {q.data && q.data.items.length === 0 && (
          <EmptyState title="No matching events" hint="Try widening filters or check ingest is running." />
        )}
        {q.data && q.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-small">
              <thead className="text-fg-muted text-eyebrow">
                <tr>
                  <th className="text-left py-2">When</th>
                  <th className="text-left">Source</th>
                  <th className="text-left">Event</th>
                  <th className="text-left">Automation</th>
                  <th className="text-left">Status</th>
                  <th className="text-right">ms</th>
                  <th className="text-right">£</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {q.data.items.map((e) => (
                  <tr key={e.id} className="align-top">
                    <td className="py-2">
                      <span title={new Date(e.occurred_at).toISOString()}>{relative(e.occurred_at)}</span>
                    </td>
                    <td className="mono">{e.source}</td>
                    <td className="mono">{e.event_type}</td>
                    <td className="mono">{e.automation_name ?? e.automation_id ?? '—'}</td>
                    <td>
                      <SevPill level={statusLevel(e.status)} label={e.status} />
                      {e.error_message && (
                        <p className="text-fg-muted text-caption mt-1 mono">{e.error_message}</p>
                      )}
                    </td>
                    <td className="text-right num">{e.duration_ms ?? '—'}</td>
                    <td className="text-right num">{e.cost_gbp != null ? e.cost_gbp.toFixed(4) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {q.data.next_cursor && (
              <div className="mt-4 text-right">
                <button type="button" onClick={() => setCursor(q.data!.next_cursor)} className="text-eyebrow border border-rule px-3 py-2">
                  Next page →
                </button>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
